import {
  CONTENT_HEALTH_STATUS_ORDER,
  ContentHealthStatus,
} from "../../../constants/status-colors";
import {
  ContentNodeType,
  TreeNode,
} from "../../../services/content-tree/content-tree.types";
import { ENTITY_TAG_COLOR } from "../../../components/CommandPalette/utils";
import { NODE_TYPE_LABEL } from "../ContentMap/utils";

// A non-active tree node status is exactly a content-health status
// (deleted | detached | broken | incomplete) — same backend vocabulary.
export type AttentionStatus = ContentHealthStatus;

/** One flat row of the attention queue: an unhealthy node plus enough
 * context (program + ancestor path) to find and fix it. */
export interface AttentionItem {
  /** Unique across programs (a shared node may appear under two programs). */
  key: string;
  programId: number;
  programTitle: string;
  type: ContentNodeType;
  id: number;
  title: string;
  status: AttentionStatus;
  statusDetail?: string;
  /** Ancestor titles from the program root down to the direct parent,
   * joined with " › ". Empty for the program root itself. */
  parentPath: string;
}

/**
 * Walks a program tree client-side and returns the flat list of nodes with
 * issues (status !== "active"). Pure: no filtering UI concerns here — the
 * health endpoint only exposes per-program counts, so the per-node detail
 * has to be flattened from the same tree the content map renders.
 */
export const collectAttentionItems = (
  root: TreeNode,
  program: { id: number; title: string }
): AttentionItem[] => {
  const items: AttentionItem[] = [];

  const walk = (node: TreeNode, ancestors: string[]) => {
    if (node.status !== "active") {
      items.push({
        key: `${program.id}:${node.key}`,
        programId: program.id,
        programTitle: program.title,
        type: node.type,
        id: node.id,
        title: node.title,
        status: node.status,
        statusDetail: node.status_detail,
        parentPath: ancestors.join(" › "),
      });
    }
    (node.children ?? []).forEach((child) =>
      walk(child, [...ancestors, node.title])
    );
  };

  walk(root, []);
  return items;
};

// Severity rank = position in the canonical status order from
// status-colors.ts (deleted > detached > broken > incomplete). Unknown
// statuses sink to the bottom instead of crashing the sort.
const severityRank = (status: AttentionStatus): number => {
  const index = CONTENT_HEALTH_STATUS_ORDER.indexOf(status);
  return index === -1 ? CONTENT_HEALTH_STATUS_ORDER.length : index;
};

/** Default ordering of the queue: most severe first; program/id as a stable
 * tiebreaker so rows of the same severity stay grouped and predictable. */
export const sortBySeverity = (items: AttentionItem[]): AttentionItem[] =>
  [...items].sort(
    (a, b) =>
      severityRank(a.status) - severityRank(b.status) ||
      a.programId - b.programId ||
      a.id - b.id
  );

export interface AttentionFilters {
  /** ContentNodeType or "" for all. */
  type: string;
  /** AttentionStatus or "" for all. */
  status: string;
}

/** Applies the (URL-committed) type/status filters. Empty string = no filter. */
export const filterAttentionItems = (
  items: AttentionItem[],
  filters: AttentionFilters
): AttentionItem[] =>
  items.filter(
    (item) =>
      (!filters.type || item.type === filters.type) &&
      (!filters.status || item.status === filters.status)
  );

// Entity badge colors: node types the command-palette preview also knows
// reuse its exact palette; the rest get their own theme-adaptive presets
// (avoiding red/orange/volcano/gold, which mean issue severity here).
export const ENTITY_BADGE_COLOR: Record<ContentNodeType, string> = {
  program: ENTITY_TAG_COLOR.program,
  course: ENTITY_TAG_COLOR.course,
  lesson: ENTITY_TAG_COLOR.lesson,
  resource: ENTITY_TAG_COLOR.resource,
  game_quiz: ENTITY_TAG_COLOR.game_quiz,
  activity: "green",
  project: "lime",
  phase: "default",
};

const capitalize = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

/** Badge label: the shared singular noun (ContentMap vocabulary),
 * capitalized. Unknown types fall back to the raw type. */
export const entityBadgeLabel = (type: ContentNodeType): string =>
  capitalize(NODE_TYPE_LABEL[type]?.singular ?? type);
